import React, { useState, useEffect } from "react";
import history from "../../managers/history";
import Banner from "../../common/components/banner";
import { useForm } from "react-hook-form";
import {
  environment,
  failureMessageConstant,
  regexConstant
} from "../../constants";
import { Loader } from "../../common/components/loader";
import CustomTooltip from "../../common/components/customTooltip";
import PropTypes from "prop-types";
import disposableDomains from "disposable-email-domains";

const {
  PASSWORD_REQUIRED,
  PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS,
  EMAIL_REQUIRED,
  INVALID_EMAIL,
  NAME_REQUIRED,
  PASSWORD_MATCH_ERROR,
  MAX_CHARS
} = failureMessageConstant;

const SignUpComponent = (props) => {
  const states = props?.states;
  const { urlEmail, urlName } = props;
  const functionObj = props?.functionObj;
  const errorMessage = props?.errorMessage;
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm();
  const [componentHeight, setComponentHeight] = useState(window.innerHeight);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const password = watch("password");
  const confirmPassword = watch("confirmPassword");

  const disposableDomainSet = new Set(disposableDomains);
  const isDisposableEmail = (email) => {
    const domain = email.split("@")[1];
    return disposableDomainSet.has(domain);
  };

  const updateComponentHeight = () => {
    setComponentHeight(window.innerHeight);
  };

  useEffect(() => {
    updateComponentHeight();
    window.addEventListener("resize", updateComponentHeight);
    return () => {
      window.removeEventListener("resize", updateComponentHeight);
    };
  }, []);

  return (
    <div
      className="flex lg:overflow-hidden "
      style={{ height: componentHeight }}
    >
      <div className="flex w-full flex-col overflow-y-auto bg-white px-5 md:w-1/2 ">
        <div className="flex pl-2 pt-3">
          <img alt="logo" className="z-10" src="/images/datasent-logo.svg" />
        </div>
        <div className="my-auto w-full pb-4 md:mx-auto md:max-w-89">
          <div className="flex font-MontserratSemiBold text-ft24 ">
            Create Datasent Business Account
          </div>
          <form
            onSubmit={handleSubmit(
              functionObj?.checkBusinessAlreadyExistsOrNot
            )}
          >
            <div className="pt-11.5">
              <div className="font-MontserratMedium text-ft15">
                Business Name
              </div>
              <div className="mb-6 mt-13px">
                <input
                  id="name"
                  name="name"
                  autoComplete="new-name"
                  placeholder="Add Your Business Name"
                  defaultValue={urlName}
                  className={`relative flex h-49px w-full rounded border ${errors?.name ? "border-red-10" : "border-grey-C7C7C7"} px-4 py-2 font-MontserratRegular text-ft15 outline-none `}
                  {...register("name", {
                    required: NAME_REQUIRED,
                    validate: (value) => {
                      const trimmedValue = value.replace(/\s+/g, "").trim();
                      if (trimmedValue.length > 30) {
                        return MAX_CHARS;
                      }
                    }
                  })}
                />
                {!!errors?.name && (
                  <div className="mt-2 flex items-center gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    <img src="/images/info-circle.svg" alt="info"></img>
                    <p>{errors?.name?.message}</p>
                  </div>
                )}
              </div>
              <div className="font-MontserratMedium text-ft15">Email</div>
              <div className="mb-7 mt-13px">
                <input
                  name="email"
                  id="email"
                  autoComplete="email"
                  placeholder="admin@gmail.com"
                  className={`relative flex h-49px w-full rounded border ${errors?.email ? "border-red-10" : "border-grey-C7C7C7"} px-4 py-2 font-MontserratRegular text-ft15 outline-none `}
                  {...register("email", {
                    required: EMAIL_REQUIRED,
                    pattern: {
                      value: regexConstant.EMAIL_REGEX,
                      message: INVALID_EMAIL
                    },
                    validate: (value) => {
                      if (
                        process.env.REACT_APP_ENV === environment.PRODUCTION &&
                        isDisposableEmail(value)
                      ) {
                        return INVALID_EMAIL;
                      }
                    }
                  })}
                  defaultValue={urlEmail}
                />
                {!!errors?.email && (
                  <div className="mt-2 flex items-center gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    <img src="/images/info-circle.svg" alt="info"></img>
                    <p>{errors?.email?.message}</p>
                  </div>
                )}
                {errorMessage && (
                  <div className="mt-2 flex items-center gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    <img src="/images/info-circle.svg" alt="info"></img>
                    <p>{errorMessage}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-1 font-MontserratMedium text-ft15">
                Password
                <CustomTooltip
                  text={PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS}
                  positionLeft={53}
                />
              </div>
              <div className="mb-6 mt-13px">
                <div
                  className={` flex h-49px items-center justify-between rounded border px-4 ${errors?.password ? "border-red-10" : "border-grey-C7C7C7"} `}
                >
                  <input
                    name="password"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Password"
                    className="size-full font-MontserratRegular text-ft15 outline-none"
                    {...register("password", {
                      required: PASSWORD_REQUIRED,
                      pattern: {
                        value: regexConstant.PASSWORD_REGEX,
                        message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                      },
                      minLength: {
                        value: 8,
                        message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                      }
                    })}
                  />
                  {password && (
                    <img
                      className="cursor-pointer"
                      onClick={() => setShowPassword(!showPassword)}
                      alt="eye"
                      src={
                        showPassword ? "/images/unmask.svg" : "/images/mask.svg"
                      }
                    />
                  )}
                </div>
                {!!errors?.password && (
                  <div className="mt-2 flex items-start gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    <img src="/images/info-circle.svg" alt="info"></img>
                    <p>{errors?.password?.message}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-1 font-MontserratMedium text-ft15">
                Confirm Password
                <CustomTooltip
                  text={PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS}
                  positionLeft={-13}
                />
              </div>
              <div className="mt-2">
                <div
                  className={` flex h-49px items-center justify-between rounded border ${errors?.confirmPassword ? "border-red-10" : "border-grey-C7C7C7"} px-4 `}
                >
                  <input
                    name="confirmPassword"
                    id="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Confirm Password"
                    className="size-full font-MontserratRegular text-ft15 outline-none"
                    {...register("confirmPassword", {
                      required: PASSWORD_REQUIRED,
                      validate: (value) =>
                        value === password || PASSWORD_MATCH_ERROR,
                      pattern: {
                        value: regexConstant.PASSWORD_REGEX,
                        message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                      },
                      minLength: {
                        value: 8,
                        message: PASSWORD_MUST_BE_AT_LEAST_8_CHARACTERS
                      }
                    })}
                  />
                  {confirmPassword && (
                    <img
                      className="cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      alt="eye"
                      src={
                        showConfirmPassword
                          ? "/images/unmask.svg"
                          : "/images/mask.svg"
                      }
                    />
                  )}
                </div>
                {!!errors?.confirmPassword && (
                  <div className="mt-2 flex items-start gap-5px font-MontserratRegular text-ft13-15.85-0.26 text-red-10">
                    <img src="/images/info-circle.svg" alt="info"></img>
                    <p>{errors?.confirmPassword?.message}</p>
                  </div>
                )}
              </div>
              {process.env.REACT_APP_ENV !== environment.PRODUCTION && (
                <div className="mt-7 flex gap-2 rounded-lg border border-grey-C7C7C7 p-2 font-MontserratMedium text-ft13-15.85-0.26 text-grey-700">
                  <img src="/images/info-purple.svg" alt="info"></img>
                  <span>
                    Please enable third-party cookies in your browser settings,
                    to proceed with smooth sign up process.
                  </span>
                </div>
              )}
              <button
                disabled={states?.loader}
                type="submit"
                className="mt-7 flex h-49px w-full items-center justify-center rounded bg-purple-6C36B0 font-MontserratMedium text-ft15 text-white "
              >
                {states?.loader ? <Loader /> : "Create Your Account"}
              </button>
              <div className="mt-14 font-MontserratRegular text-ft13-15.85-0.26 text-black-000000 md:text-ft15 xl:whitespace-nowrap">
                By signing up, you agree to our communications and usage terms
              </div>
              <div className="flex items-center justify-between font-MontserratRegular">
                <div className="mt-21px flex justify-end text-ft13-15.85-0.26 text-black-10 md:text-ft15">
                  Already a user?
                  <div
                    onClick={() => history.push("/")}
                    className="ml-1 cursor-pointer text-ft13-15.85-0.26 text-blue-726FFF md:text-ft15 "
                  >
                    Sign In
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      <Banner />
    </div>
  );
};
SignUpComponent.propTypes = {
  states: PropTypes.shape({
    userConsentData: PropTypes.array.isRequired,
    page: PropTypes.number.isRequired,
    totalItems: PropTypes.number.isRequired,
    itemsPerPage: PropTypes.number.isRequired
  }).isRequired,

  urlEmail: PropTypes.string.isRequired,
  urlName: PropTypes.string.isRequired,
  functionObj: PropTypes.shape({
    handleChangePage: PropTypes.func.isRequired,
    setState: PropTypes.func.isRequired
  }).isRequired,

  errorMessage: PropTypes.string
};
export default SignUpComponent;
